import { W8BENFormModel, W8BENResponseModel, W8BENFieldMapper } from "../../../Models/W8BENModels";
import { W8BENService } from "../../../services/W8BENService";
import { getUserIdFromLocalStorage } from "../../../API_Endpoints/APIEndpoints";
import { pythonBaseUrl } from "../../../App_Settings/app_settings";
import { normalizeDateToSlash } from "../../../Utility/utility";

import { getStateCode } from "../../../utils/formUtils";

// Document type mapping with TypeID and Type
const documentTypeMapping: Record<string, { typeId: string; type: string }> = {
  partnership_agreement: {
    typeId: "74B29767-B166-49A1-90C6-04D919FFF021",
    type: "Partnership Agreement",
  },
  trust_deed: {
    typeId: "E0E34437-CC13-487C-863E-248204E61086",
    type: "Trust Deed or Certified Extracts",
  },
  certificate_registration: {
    typeId: "F0B2D5EC-5C24-4FE5-A571-2610610FA3D1",
    type: "Certificate of Registration",
  },
  certificate_incorporation: {
    typeId: "F113A468-ADE7-4C20-82C4-33A496456F54",
    type: "Certificate of Incorporation",
  },
  armed_forces_card: { typeId: "5EBD9B1F-BEB2-4BB5-BCB9-35C105F278A5", type: "Armed Forces Card" },
  limitations_benefits: {
    typeId: "5309E91F-6B24-4183-9C7F-3A062658E625",
    type: "Limitations of Benefits Statement",
  },
  election_form: {
    typeId: "DB6649B1-F4BE-41D5-AAAD-4588B253C39D",
    type: "Election form - line 9 option",
  },
  public_register: {
    typeId: "58740ED7-F2EF-481D-B0FA-4703F257B1C6",
    type: "Public Register Extracts",
  },
  driving_license: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  driverLicense: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  state_eu: { typeId: "D525BDE1-E0A2-4F2A-B2CE-5AD75383264E", type: "State of the EU" },
  email: { typeId: "C2676B9E-BCFA-497E-8588-6B8B1DA00914", type: "eMail" },
  retroactive_statement: {
    typeId: "3DC45284-ACA4-494B-AA1F-6EBA8736F9E1",
    type: "Retroactive Statement",
  },
  birth_certificate: { typeId: "70612B92-D0F3-4347-9CC3-912B9597C800", type: "Birth Certificate" },
  passport: { typeId: "70825FED-AEBF-4EB8-949D-998D69D718F9", type: "Passport" },
  withholding_statement: {
    typeId: "872ACA95-E43E-4EFA-A494-9A4A97356365",
    type: "Withholding Statement",
  },
  email_alt: { typeId: "325FFDDE-2558-4E32-B7DA-A67435BF59F9", type: "eMail" },
  other: { typeId: "E697B03A-91D1-4CEB-8EC4-A7A4E53E5B30", type: "Other" },
  hybrid_status: {
    typeId: "5A447556-7724-484B-A6D4-BAE392B8FF22",
    type: "Hybrid Status - Supporting Documentation",
  },
  notional_principle: {
    typeId: "BB810C57-8652-4431-BEB2-BE50057C17DC",
    type: "Notional principle contracts statement",
  },
  connected_documentation: {
    typeId: "ACCDB44A-2518-4B06-95C2-BF837E64E343",
    type: "Connected documentation",
  },
  national_id_member: {
    typeId: "4EA07713-50FD-4427-AB0A-C3972E3A7BB0",
    type: "National ID Card of A Member",
  },
  form_w4: { typeId: "D9AFD794-205A-45C3-A64A-C5219F20CBB2", type: "Form W-4" },
  proof_residency: {
    typeId: "F1BC6DC5-19C6-441F-A133-CEDE22778968",
    type: "Proof of Residency Document",
  },
  affidavit: { typeId: "B6477F29-2FE4-4CC0-A58E-CF7B91F791E7", type: "Affidavit" },
  national_id: { typeId: "405AE4A8-A1FA-45EB-9449-D1C76EC101FB", type: "National ID Card" },
  effectively_connected: {
    typeId: "BD2028C8-56A5-4404-895A-D36F42535DDB",
    type: "Effectively connected income statement - line 9 option",
  },
  power_attorney: {
    typeId: "59AC92CD-62C8-46DC-BC33-DBAFA0716242",
    type: "Power of attorney statement",
  },
  us_counsel: {
    typeId: "B6A5ABBA-E61E-4C5C-9B43-E051D75A26F6",
    type: "U.S. counsel opinion statement",
  },
  work_visa: { typeId: "1F7944EA-CF76-40FB-B182-E3D1DE59A74A", type: "Work visa" },
  expatriate: { typeId: "7AE8E536-21FD-46A2-93B7-EE99477D7DE2", type: "Expatriate Documentation" },
};

// Helper function to get document type info
const getDocumentTypeInfo = (documentType: string) => {
  // Normalize the document type by removing common prefixes and converting to lowercase
  const normalizedType = documentType
    .toLowerCase()
    .replace(/^idDocument\./, "")
    .replace(/\.pdf$/, "");

  // Try direct match first
  if (documentTypeMapping[normalizedType]) {
    return documentTypeMapping[normalizedType];
  }

  // Try partial matches for common document types
  if (normalizedType.includes("passport")) {
    return documentTypeMapping["passport"];
  }
  if (normalizedType.includes("driver") || normalizedType.includes("license")) {
    return documentTypeMapping["driving_license"];
  }
  if (normalizedType.includes("national") && normalizedType.includes("id")) {
    return documentTypeMapping["national_id"];
  }

  // Default to 'Other' if no match found
  return documentTypeMapping["other"];
};

export async function getPDFBase64(url: string, mainFileImportId?: string): Promise<string> {
  try {
    // Get authentication token for secured requests
    const userData =
      typeof getUserIdFromLocalStorage === "function"
        ? getUserIdFromLocalStorage()
        : JSON.parse(localStorage.getItem("token") || "{}");

    const headers: HeadersInit = {};

    // Add authentication if token is available
    if (userData?.token) {
      headers["Authorization"] = `Bearer ${userData.token}`;
    }

    // Handle different URL formats for ID documents
    let fetchUrl = url;

    // Check if this is an ID document URL (viewPdf format: "orgId/userId/uploadfiles/filename.pdf" or "orgId/userId/businessUnit/split_documents/fileImportId/filename.pdf")
    if (
      (url.includes("/uploadfiles/") || url.includes("/split_documents/")) &&
      !url.startsWith("http")
    ) {
      // Parse the viewPdf URL structure:
      // - Regular: "orgId/userId/uploadfiles/filename.pdf"
      // - Split documents: "orgId/userId/businessUnit/split_documents/fileImportId/filename.pdf"
      const urlParts = url.split("/");
      const orgIdFromUrl = urlParts[0];
      const userIdFromUrl = urlParts[1];
      const fileName = urlParts[urlParts.length - 1]; // Full filename with .pdf

      let fileImportId;
      let businessUnit = "";
      let fileType = "uploadfiles"; // Default to uploadfiles for all non-split documents

      if (url.includes("/split_documents/")) {
        // For split documents: orgId/userId/businessUnit/split_documents/fileImportId/filename.pdf
        businessUnit = urlParts[2]; // Business unit is the 3rd part
        fileImportId = urlParts[4]; // FileImportId is the 5th part (index 4)
        fileType = "split_documents";
      } else {
        // For regular uploadfiles: orgId/userId/uploadfiles/filename.pdf
        fileImportId = mainFileImportId || fileName.replace(".pdf", "");
        fileType = "uploadfiles";
      }

      // Use organizationId and userId from URL if available, otherwise fallback to user data
      const organizationId = orgIdFromUrl || userData?.organizationId;
      const userId = userIdFromUrl || userData?.userId;

      if (organizationId && userId) {
        // Use the same secure API as SecureDocumentViewer for consistency
        fetchUrl = `${pythonBaseUrl}/File/GetFileBytes`;

        // Match the exact parameters used by getFileBytes API
        const requestData = {
          organizationId: organizationId,
          userId: userId,
          fileImportId: fileImportId,
          filename: fileName,
          extension: "pdf",
          type: fileType,
          businessunit: businessUnit,
        };

        const response = await fetch(fetchUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: headers["Authorization"],
            Accept: "application/octet-stream",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(
            `Secure document fetch failed: ${response.status} ${response.statusText}`
          );
        }

        const arrayBuffer = await response.arrayBuffer();
        // Convert ArrayBuffer to base64
        const uint8Array = new Uint8Array(arrayBuffer);
        const binaryString = Array.from(uint8Array, (byte) => String.fromCharCode(byte)).join("");
        const base64data = btoa(binaryString);

        return base64data;
      }
    }

    // For regular URLs, use direct fetch with authentication

    const response = await fetch(fetchUrl, { headers });

    if (!response.ok) {
      throw new Error(`PDF fetch failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result?.toString().split(",")[1];
        resolve(base64data || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("❌ Error fetching PDF:", error);
    console.error("URL that failed:", url);
    return "";
  }
}

/**
 * Generate W8BEN XML from input data
 * Uses W8BEN models for consistent field handling
 */
export const generateW8BENXML = async (inputData: any) => {
  // Create form model from input data using proper mapping
  let formModel: W8BENFormModel;

  // Check if data has already been mapped (isMapDone or isMapDone flag)
  const isMapped = inputData?.isMapDone === true || inputData?.isMapDone === true;

  if (inputData instanceof W8BENFormModel) {
    // If already a form model, use it directly

    formModel = inputData;
  } else if (!isMapped) {
    formModel = W8BENService.fromApiResponse(inputData);
  } else {
    // Data is already mapped, just convert to form model

    const responseModel = new W8BENResponseModel(inputData);
    formModel = W8BENFieldMapper.mapResponseToForm(responseModel);

    // Handle any additional fields from input data that might not be in the response model
    Object.keys(inputData).forEach((key) => {
      if (inputData[key] !== undefined && !formModel.hasOwnProperty(key)) {
        (formModel as any)[key] = inputData[key];
      }
    });
  }

  // Helper function to safely get values from form model
  const getValue = (key: string, defaultValue: string = "") =>
    (formModel as any)[key] || defaultValue;

  // Helper function to convert boolean to string representation
  const getBooleanValue = (key: string) => {
    const value = (formModel as any)[key];
    // Handle string "selected" value
    if (value === "selected") {
      return "True";
    }

    // Handle boolean true/false
    if (value === true) {
      return "True";
    }
    if (value === "yes") {
      return "True";
    }

    return "False";
  };
  console.log(getValue("isSigned"));
  // Generate related documents XML
  const generateRelatedDocumentsXml = async () => {
    let relatedDocumentsXml = "";

    // Handle single document case from original code
    if ((formModel as any).idDocumentUrl) {
      const docContent = await getPDFBase64((formModel as any).idDocumentUrl);
      const docTypeInfo = getDocumentTypeInfo((formModel as any).idDocumentType || "other");

      relatedDocumentsXml += `
      <RelatedDocument>
        <Type>${docTypeInfo.type}</Type>
        <TypeID>${docTypeInfo.typeId}</TypeID>
        <File>
          <Name>${(formModel as any).idDocumentType || "id_document"}.pdf</Name>
          <Data>${docContent}</Data>
        </File>
      </RelatedDocument>`;
    }

    // Handle multiple documents if provided in docs array
    if ((formModel as any).docs && Array.isArray((formModel as any).docs)) {
      for (const doc of (formModel as any).docs) {
        if (doc.url) {
          // Make sure to await the PDF base64 conversion
          const docContent = await getPDFBase64(doc.url);
          const docTypeInfo = getDocumentTypeInfo(doc.type || "other");

          relatedDocumentsXml += `
          <RelatedDocument>
            <Type>${docTypeInfo.type}</Type>
            <TypeID>${docTypeInfo.typeId}</TypeID>
            <File>
              <Name>${doc.name || "document.pdf"}</Name>
              <Data>${docContent}</Data>
            </File>
          </RelatedDocument>`;
        }
      }
    }
    // Handle withholding statement document (attached to first form matching first row of excel)
    if ((formModel as any).withholdingStatementUrl) {
      try {
        const whsContent = await getPDFBase64((formModel as any).withholdingStatementUrl);
        const whsTypeInfo = getDocumentTypeInfo("withholding_statement");

        relatedDocumentsXml += `
      <RelatedDocument>
        <Type>${whsTypeInfo.type}</Type>
        <TypeID>${whsTypeInfo.typeId}</TypeID>
        <File>
          <Name>Withholding Statement.pdf</Name>
          <Data>${whsContent}</Data>
        </File>
      </RelatedDocument>`;
      } catch (err) {
        console.error("Failed to attach Withholding Statement:", err);
      }
    }

    return relatedDocumentsXml;
  };

  const relatedDocumentsXml = await generateRelatedDocumentsXml();

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf", "");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, (formModel as any).fileImportId);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Use separate first and last name fields if available, otherwise split full name
  let firstName = getValue("First name and middle name", "");
  let lastName = getValue("Last name", "");

  // If separate fields are empty, fall back to splitting the full name
  if (!firstName && !lastName) {
    const fullName = getValue("Beneficial Owner name", "");
    const nameParts = fullName.split(" ");
    firstName = nameParts[0] || "";
    lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
  }

  const xml = `
  <W_8BEN_OCT_2021>
    <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
    <UIDreferenceline>False</UIDreferenceline>
    <Instructor_Identifier>${getValue("entityUniqueIdentifier")}</Instructor_Identifier>
    <BusinessUnit>${getValue("BusinessUnit", "")}</BusinessUnit>
    <ElectronicRecipientStatement>True</ElectronicRecipientStatement>
    <ElectronicRecipientStatementDate></ElectronicRecipientStatementDate>
    <Entity></Entity>
    <CommonInfo>
      <EntityTypes>
        <EntityType></EntityType>
      </EntityTypes>
      <EntityClassification/>
      <InitialEntityTypeSelection>Non U.S. Individual</InitialEntityTypeSelection>
      <FATCAEntityType/>
      <Individual>
        <FirstName>${firstName}</FirstName>
        <LastName>${lastName}</LastName>
        <CountryOfBirth>${getValue("Country of citizenship", "")}</CountryOfBirth>
        <CountryOfBirthOther/>
        <DateOfBirth>${normalizeDateToSlash(getValue("Date of birth", ""))}</DateOfBirth>
        <DateOfBirthOnForm>${getValue("Date_of_birth_on_form", "") || getValue("Date of birth")}</DateOfBirthOnForm>
        <CountryOfCitizenshipForTax>${getValue("Country of citizenship", "")}</CountryOfCitizenshipForTax>
        <CountryOfCitizenshipForTaxOther/>
        <CityTownOfBirth/>
        <PlaceofBirth/>
        <AdditionalTaxJurisdictions/>
      </Individual>
      <Business>
        <Name/>
        <Country>${getValue("Country_of_incorporation/organization", "")}</Country>
        <CountryOther/>
        <DoingBusinessAsName/>
        <DREFATCAEntitytype/>
        <JurisdictionTaxCountry/>
        <SponsoringEntityName/>
        <SponsoringEntityGIIN>${getValue("BEN_9_GIIN", "")}</SponsoringEntityGIIN>
        <TrusteeName/>
        <TrusteeGIIN/>
        <AdditionalTaxJurisdictions/>
        <IsSpecifiedUSPerson>False</IsSpecifiedUSPerson>
        <SpecifiedPersonUS>NA</SpecifiedPersonUS>
        <IsSpecifiedUKPerson>False</IsSpecifiedUKPerson>
        <SpecifiedPersonUK>NA</SpecifiedPersonUK>
        <IsSpecifiedCRSPerson>False</IsSpecifiedCRSPerson>
        <SpecifiedPersonCRS>NA</SpecifiedPersonCRS>
      </Business>
      <PermanentAddress>
        <Line1>${getValue("Permanent residence address", "") || getValue("Permanent residence address", "")}</Line1>
        <Line2>${getValue("Permanent address line 2", "")}</Line2>
        <Line3>${getValue("Permanent address line 3", "")}</Line3>
        <City_Town>${getValue("Permanent address city", "") || getValue("Permanent city address", "")}</City_Town>
        <State_Province>${getStateCode(getValue("Permanent address state NonUS", "") || getStateCode(getValue("Permanent address state USCA", "")))}</State_Province>
        <Country>${getValue("Permanent address country", "")}</Country>
        <CountryOther/>
        <Zip_PostalCode>${getValue("Permanent address zip", "")}</Zip_PostalCode>
        <IsCare_of_address>${getBooleanValue("Permanent address is care of")}</IsCare_of_address>
        <IsP_O_Box>${getBooleanValue("Permanent address is po box")}</IsP_O_Box>
        <IsRuralRoute>${getBooleanValue("Permanent address is rural route")}</IsRuralRoute>
      </PermanentAddress>
      <MailingAddress>
        <Line1>${getValue("Mailing address", "")}</Line1>
        <Line2>${getValue("Mailing address line 2", "")}</Line2>
        <Line3>${getValue("Mailing address line 3", "")}</Line3>
        <City_Town>${getValue("Mailing city address", "")}</City_Town>
        <State_Province>${getStateCode(getValue("Mailing address state NonUS", "") || getStateCode(getValue("Mailing address state USCA", "")))}</State_Province>
        <Country>${getValue("Mailing address country", "")}</Country>
        <CountryOther/>
        <Zip_PostalCode>${getValue("Mailing address zip", "")}</Zip_PostalCode>
        <IsCare_of_address>${getBooleanValue("Mailing address is care of")}</IsCare_of_address>
        <IsP_O_Box>${getBooleanValue("Mailing address is po box")}</IsP_O_Box>
        <IsRuralRoute>${getBooleanValue("Mailing address is rural route")}</IsRuralRoute>
      </MailingAddress>
      <ContactInfo>
        <Email>${getValue("Taxpayer email", "")}</Email>
        <TelephoneCountry/>
        <TelephoneNumber/>
        <CellNumber/>
      </ContactInfo>
      <SubsidiaryInfo>
        <SubsidiaryId/>
        <SubsidiaryName/>
        <SubsidiaryContactName/>
        <SubsidiaryContactTelephone/>
        <SubsidiaryContactEmail/>
      </SubsidiaryInfo>
      <HybridStatus>Not Applicable</HybridStatus>
      <HybridStatusDescription/>
      <HybridStatusSupportingDocumentationAttached>False</HybridStatusSupportingDocumentationAttached>
      <IsOnBehalfOfSomeoneElse>False</IsOnBehalfOfSomeoneElse>
      <Proxy>
        <FirstName>${firstName}</FirstName>
        <LastName>${lastName}</LastName>
        <Capacity>${(formModel as any)["Capacity to sign"] ? "Legal" : ""}</Capacity>
        <CapacityOther/>
        <CountryAlternative/>
        <AlterNativeNumber/>
        <CountryAlternativeII/>
        <AlterNativeNumberII/>
      </Proxy>
      <NoUSSiDeclarationConfirmed/>
      <NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
      <IgnoredRules>
        <IgnoredRule>
          <Rule>SIG112</Rule>
          <Comment/>
        </IgnoredRule>
      </IgnoredRules>
      <NonUSIndividual_IsClaimingECI/>
      <NonUSIndividual_IsForIndependentPersonalServices/>
      <NonUSIndividual_IsPublicEntertainer/>
      <NonUSIndividual_IsResidentNoSpecificProvision/>
      <NonUSIndividual_IsForDependentPersonalServices/>
      <NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome/>
      <NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption/>
      <NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption/>
      <NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome/>
      <NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits/>
      <SelectedOptionsonGuide4/>
    </CommonInfo>
    <TaxpayerIdentification>
      <Type>${getValue("Taxpayer type", "") || (getValue("U.S. taxpayer identification number", "") ? "SSN/ITIN" : "")}</Type>
      <SSNOrITIN>${getValue("U.S. taxpayer identification number", "")}</SSNOrITIN>
      <GIINAppliedFor>False</GIINAppliedFor>
      <GIINNotApplicable/>
      <ForeignTaxpayerId>${getValue("Foreign tax identifying number", "")}</ForeignTaxpayerId>
      <TINFormatNotAvail>False</TINFormatNotAvail>
      <ForeignTaxpayerCountry>${getValue("Country of citizenship", "")}</ForeignTaxpayerCountry>
      <ForeignTinUnavailable>${getBooleanValue("W8BEN-FTIN_not_required")}</ForeignTinUnavailable>
      <ForeignTinUnavailableReason/>
      <IsAppliedForTIN>False</IsAppliedForTIN>
      <UnexpectedTINTypeExplanation/>
      <UnexpectedTINTypeExplanationConfirmed/>
      <UnexpectedTINTypeContentBlock/>
      <CheckifFTINnotlegallyrequired>${getBooleanValue("W8BEN-FTIN_not_required")}</CheckifFTINnotlegallyrequired>
      <IsFurtherExplanation>False</IsFurtherExplanation>
      <WrittenStatementReasons/>
      <WrittenStatementReasonsAdditionalInformation/>
      <TINCheck>
        <IsCheckPreformed>False</IsCheckPreformed>
        <IsSuccessful>False</IsSuccessful>
        <Date/>
      </TINCheck>
    </TaxpayerIdentification>
    <SignInfo>
      <IsSigned>${getBooleanValue("isSigned")}</IsSigned>
      <By>${getValue("Signer_printed_name", "")}</By>
      <Capacity>${(formModel as any)["Capacity to sign"] ? "Legal" : ""}</Capacity>
      <Date>${normalizeDateToSlash(getValue("Signature_date", ""))}</Date>
      <DateOnForm>${getValue("Signature_date_on_form", "") || getValue("Signature_date", "")}</DateOnForm>
      <SignatureUnreadable>${getBooleanValue("Signature_unreadable")}</SignatureUnreadable>
    </SignInfo>
    <Tag>
      <SubmissionDate></SubmissionDate>
      <IsFromComplyExchangeService>False</IsFromComplyExchangeService>
      <IsFromBureau>False</IsFromBureau>
      <IsPaperForm>False</IsPaperForm>
      <IsE_form>False</IsE_form>
      <IsMobileSubmission>False</IsMobileSubmission>
      <RetroactiveStatement>
        <IsSigned>False</IsSigned>
        <SignedBy/>
        <SignDate></SignDate>
        <EffectiveDate></EffectiveDate>
        <ChangeinCircumstanceDate/>
        <ReasonForChange/>
        <Explanation/>
        <RetroSignBy/>
        <StatementText>I declare, under penalties of perjury, that I have examined the Form W-8BEN submission that I am providing and that the information and certifications contained therein remain the same and unchanged throughout the period beginning on 01/01/2014 to the date of the submission here and that they were true, correct and complete during the entire period.&amp;lt;br /&amp;gt;&amp;lt;br /&amp;gt;If any information or certifications were not true, correct and complete for that entire period enter the date from when they were, providing additional written information below supporting the changes in circumstances back to the account opening date provided above.</StatementText>
      </RetroactiveStatement>
    </Tag>
    <ReferenceNumbers>${getValue("W8BEN-Reference_numbers", "")}</ReferenceNumbers>
    <IsClaimed9>${getValue("W8BEN-Treaty_country", "") ? "True" : "False"}</IsClaimed9>
    <ClaimedCountry>${getValue("W8BEN-Treaty_country", "")}</ClaimedCountry>
    <ClaimedCountryOther/>
    <ClaimedExplanation>${getValue("W8BEN-Treaty_conditions_explanation", "") || getValue("W8BEN-Treaty_conditions_explanation_0", "")}</ClaimedExplanation>
    <MultipleSRCsPossible>True</MultipleSRCsPossible>
    <SRCs>
      <SRC>
        <ClaimedArticle>${getValue("W8BEN-Treaty_article", "") || getValue("W8BEN-Treaty_article_0", "")}</ClaimedArticle>
        <ClaimedPercentage>${getValue("W8BEN-Treaty_withholding_rate", "") || getValue("W8BEN-Treaty_withholding_rate_0", "")}</ClaimedPercentage>
        <ClaimedTypeOfIncome>${getValue("W8BEN-Treaty_income_type", "") || getValue("W8BEN-Treaty_income_type_0", "")}</ClaimedTypeOfIncome>
        <Status></Status>
        <ClaimedParagraph>${getValue("Treaty_paragraph_0", "")}</ClaimedParagraph>
        <ClaimedSubparagraph>${getValue("Treaty_subparagraph_0", "")}</ClaimedSubparagraph>
      </SRC>
    </SRCs>
    <RelatedDocuments>
      ${relatedDocumentsXml}
    </RelatedDocuments>
    ${originalFileXml}
    <SignatureCertify>${getBooleanValue("Signature_capacity")}</SignatureCertify>
    
    <Form_OCR_Signature/> 
  </W_8BEN_OCT_2021>`;

  console.log(xml);

  return xml;
};
